import { checkType, CtyShowToast, type AddFieldDraft } from '@circuitry-ai/doc-ui';
import {
    useCreateAiWorkerConfigurationMutation,
    useUpdateAiWorkerConfigurationMutation,
    useEntityResourcePermissions,
} from '@circuitry-ai/doc-data';
import { useEffect, useMemo, useRef, useState } from 'react';
import { deepClone } from './agentutils';
import {
    DEFAULT_ADD_FIELD_DRAFT,
    parseDifyConfigurationSections,
    resolveAddFieldValue,
    resolveConfigId,
    buildNestedConfigSection,
    getByPath,
    setByPath,
    deleteByPath,
    areJsonValuesEqual,
    isObjectValue,
    type JsonValue,
} from './agentConfigurationShared';
import { useAgentConfigurationQuery } from './useAgentConfigurationQuery';
import {
    EMPTY_DIFY_CONFIG,
    getAddFieldPathKey,
    getAgentIdentity,
    getConfigurationErrorMessage,
    passthroughLabel,
    type DifyConfigurationProps,
} from './difyConfigurationUtils';

export function useDifyConfiguration({
    agent,
    configId,
    configVersion,
    configVersionId,
    hasConfigurationVersions = false,
    activeVersionConfigurationId = '',
    onVersionUpdated,
}: DifyConfigurationProps) {
    const { aiworkerId, agentName } = getAgentIdentity(agent);
    const { canUpdate } = useEntityResourcePermissions('agent', aiworkerId || undefined);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [configState, setConfigState] = useState<Record<string, JsonValue>>(EMPTY_DIFY_CONFIG);
    const [initialConfigState, setInitialConfigState] = useState<Record<string, JsonValue>>(EMPTY_DIFY_CONFIG);
    const [expandAll, setExpandAll] = useState(false);
    const [accordionVersion, setAccordionVersion] = useState(0);
    const [flatUpdates, setFlatUpdates] = useState<Record<string, JsonValue>>({});
    const [previewModeByPath, setPreviewModeByPath] = useState<Record<string, boolean>>({});
    const [addFieldDraftByPath, setAddFieldDraftByPath] = useState<Record<string, AddFieldDraft>>({});
    const [currentSection, setCurrentSection] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [pendingDeletePath, setPendingDeletePath] = useState<string[] | null>(null);
    const [saveConfiguration, { isLoading: isSaving }] = useUpdateAiWorkerConfigurationMutation();
    const [deleteConfiguration, { isLoading: isDeleting }] = useUpdateAiWorkerConfigurationMutation();
    const [addFieldConfiguration, { isLoading: isAddingField }] = useUpdateAiWorkerConfigurationMutation();
    const [createConfiguration, { isLoading: isCreatingConfiguration }] = useCreateAiWorkerConfigurationMutation();

    const {
        selectedConfiguration,
        selectedConfigurationSignature,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        refetch,
    } = useAgentConfigurationQuery({
        aiworkerId,
        configId,
        configVersion,
        configVersionId,
        canUpdate,
    });

    const selectedConfigurationRef = useRef(selectedConfiguration);
    selectedConfigurationRef.current = selectedConfiguration;

    const parsedDifyConfiguration = useMemo(
        () => parseDifyConfigurationSections(selectedConfigurationRef.current),
        [selectedConfigurationSignature]
    );
    const {
        rootConfigurationId,
        difyPathPrefix,
        difyDeletePathPrefix,
        difyConfigurationId,
    } = parsedDifyConfiguration;
    const requiresCreateConfiguration = !hasConfigurationVersions;
    const patchConfigurationId = difyConfigurationId || rootConfigurationId || activeVersionConfigurationId;

    useEffect(() => {
        const { difyConfig: nextDifyConfig } = parseDifyConfigurationSections(selectedConfigurationRef.current);
        const nextConfig = deepClone(nextDifyConfig);
        setConfigState(nextConfig);
        setInitialConfigState(nextConfig);
        setFlatUpdates({});
        setAddFieldDraftByPath({});
        setPreviewModeByPath({});
    }, [selectedConfigurationSignature]);

    const sectionKeys = useMemo(() => Object.keys(configState), [configState]);
    const hasSections = sectionKeys.length > 0;
    const showEmptyConfigurationState = !isLoading && !isFetching && !isError && !hasSections;

    useEffect(() => {
        if (sectionKeys.length === 0) {
            setCurrentSection((prev) => (prev === '' ? prev : ''));
            return;
        }
        setCurrentSection((prev) => (sectionKeys.includes(prev) ? prev : sectionKeys[0]));
    }, [sectionKeys]);

    useEffect(() => {
        const media = globalThis.matchMedia('(max-width: 767px)');
        const handler = () => setIsCollapsed(media.matches);
        handler();
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, []);

    const updateField = (path: string[], value: JsonValue) => {
        if (!isConfigurationEditable) return;
        const pathKey = `${difyPathPrefix}.${path.join('.')}`;
        const initialValue = getByPath(initialConfigState, path);

        setConfigState((prev) => setByPath(prev, path, value) as Record<string, JsonValue>);
        setFlatUpdates((prev) => {
            const nextUpdates = { ...prev };
            if (areJsonValuesEqual(value, initialValue)) {
                delete nextUpdates[pathKey];
            } else {
                nextUpdates[pathKey] = value;
            }
            return nextUpdates;
        });
    };

    const handleToggleExpandAll = () => {
        setExpandAll((prev) => !prev);
        setAccordionVersion((prev) => prev + 1);
    };

    const handleResetAll = () => {
        if (!isConfigurationEditable) return;
        setConfigState(deepClone(initialConfigState));
        setFlatUpdates({});
        setPreviewModeByPath({});
    };

    const handleSave = async () => {
        if (!isConfigurationEditable || !patchConfigurationId || Object.keys(flatUpdates).length === 0) return;
        try {
            await saveConfiguration({
                configurationId: patchConfigurationId,
                payload: {
                    scope: 'tenant',
                    ...(flatUpdates as Record<string, unknown>),
                },
            }).unwrap();
            setInitialConfigState(deepClone(configState));
            setFlatUpdates({});
            CtyShowToast(checkType.success, 'Workflow configuration saved successfully.');
            onVersionUpdated?.();
        } catch (error: unknown) {
            CtyShowToast(checkType.error, getConfigurationErrorMessage(error, 'Failed to save workflow configuration.'));
        }
    };

    const handleFieldDelete = (path: string[]) => {
        if (!path.length || !isConfigurationEditable) return;
        setPendingDeletePath(path);
        setIsDeleteModalOpen(true);
    };

    const openAddFieldDraft = (path: string[]) => {
        if (!isConfigurationEditable) return;
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => ({
            ...prev,
            [pathKey]: DEFAULT_ADD_FIELD_DRAFT(),
        }));
    };

    const clearAddFieldDraft = (path: string[]) => {
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => {
            const next = { ...prev };
            delete next[pathKey];
            return next;
        });
    };

    const updateAddFieldDraft = (path: string[], updater: (draft: AddFieldDraft) => AddFieldDraft) => {
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => {
            const existing = prev[pathKey];
            if (!existing) return prev;
            return { ...prev, [pathKey]: updater(existing) };
        });
    };

    const addFieldAtPath = async (path: string[]) => {
        if (!isConfigurationEditable) return;
        const pathKey = getAddFieldPathKey(path);
        const draft = addFieldDraftByPath[pathKey];
        if (!draft) return;

        const trimmedKey = draft.keyName.trim();
        if (!trimmedKey) {
            CtyShowToast(checkType.error, 'Key is required.');
            return;
        }

        const targetNode = (path.length ? getByPath(configState, path) : configState) as JsonValue;
        if (!isObjectValue(targetNode)) {
            CtyShowToast(checkType.error, 'Unable to add field to this section.');
            return;
        }
        if (Object.prototype.hasOwnProperty.call(targetNode, trimmedKey)) {
            CtyShowToast(checkType.error, 'A field with this key already exists.');
            return;
        }

        const nextValue = resolveAddFieldValue(draft, passthroughLabel);
        if (nextValue === null) return;

        let createConfigId: string | undefined;
        if (requiresCreateConfiguration) {
            const configIdValue = resolveConfigId(draft, passthroughLabel);
            if (!configIdValue) return;
            createConfigId = configIdValue;
        } else if (!patchConfigurationId) {
            CtyShowToast(checkType.error, 'Unable to add configuration. Active version configuration ID is missing.');
            return;
        }

        const updatePath = [...path, trimmedKey];
        const updatePathKey = `${difyPathPrefix}.${updatePath.join('.')}`;

        try {
            if (requiresCreateConfiguration) {
                await createConfiguration({
                    aiworkerId,
                    agentName,
                    configId: createConfigId!,
                    config: {
                        dify_config: buildNestedConfigSection(path, trimmedKey, nextValue),
                    },
                }).unwrap();
            } else {
                await addFieldConfiguration({
                    configurationId: patchConfigurationId,
                    payload: {
                        scope: 'tenant',
                        [updatePathKey]: nextValue,
                    },
                }).unwrap();
            }

            await refetch();
            clearAddFieldDraft(path);
            if (path.length === 0) {
                setCurrentSection(trimmedKey);
            }
            CtyShowToast(checkType.success, 'Field added successfully.');
            onVersionUpdated?.();
        } catch (error: unknown) {
            CtyShowToast(checkType.error, getConfigurationErrorMessage(error, 'Failed to add field.'));
        }
    };

    const handleConfirmDelete = async () => {
        if (!isConfigurationEditable || !pendingDeletePath?.length || !patchConfigurationId) return;

        const pathKey = `${difyPathPrefix}.${pendingDeletePath.join('.')}`;
        const deletedPath = `${difyDeletePathPrefix}.${pendingDeletePath.join('.')}`;

        try {
            await deleteConfiguration({
                configurationId: patchConfigurationId,
                payload: {
                    scope: 'tenant',
                    'deleted.config': [deletedPath],
                },
            }).unwrap();

            setConfigState((prev) => deleteByPath(prev, pendingDeletePath) as Record<string, JsonValue>);
            setInitialConfigState((prev) => deleteByPath(prev, pendingDeletePath) as Record<string, JsonValue>);
            setFlatUpdates((prev) => {
                const nextUpdates = { ...prev };
                delete nextUpdates[pathKey];
                return nextUpdates;
            });
            setPreviewModeByPath((prev) => {
                const next = { ...prev };
                delete next[pendingDeletePath.join('.')];
                return next;
            });
            CtyShowToast(checkType.success, 'Field deleted successfully.');
            onVersionUpdated?.();
            setIsDeleteModalOpen(false);
            setPendingDeletePath(null);
        } catch (error: unknown) {
            CtyShowToast(checkType.error, getConfigurationErrorMessage(error, 'Failed to delete field.'));
        }
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setPendingDeletePath(null);
    };

    return {
        aiworkerId,
        isCollapsed,
        setIsCollapsed,
        configState,
        initialConfigState,
        expandAll,
        accordionVersion,
        flatUpdates,
        previewModeByPath,
        setPreviewModeByPath,
        addFieldDraftByPath,
        currentSection,
        setCurrentSection,
        isDeleteModalOpen,
        pendingDeletePath,
        isDeleting,
        isSaving,
        isAddingField: isAddingField || isCreatingConfiguration,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        requiresCreateConfiguration,
        patchConfigurationId,
        difyDeletePathPrefix,
        sectionKeys,
        hasSections,
        showEmptyConfigurationState,
        updateField,
        handleToggleExpandAll,
        handleResetAll,
        handleSave,
        handleFieldDelete,
        openAddFieldDraft,
        clearAddFieldDraft,
        updateAddFieldDraft,
        addFieldAtPath,
        handleConfirmDelete,
        closeDeleteModal,
    };
}
